<template>
    <div class="upload-wrapper">
        <a-upload
            :accept="accept"
            :disabled="isShow"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :maxCount="count"
            multiple
            list-type="picture"
            :file-list="ID_image"
            :before-upload="beforeUpload"
            @preview="handlePreview"
            @change="handleChange"
            name="file">
            <a-button v-if="!isShow">
                <a-icon type="upload" /> 上传文件
            </a-button>
        </a-upload>
        <!-- 图片预览弹窗 -->
        <a-modal v-model="previewVisible" :footer="null" @cancel="handleCancel">
            <img alt="preview" style="width: 100%" :src="previewImage" />
        </a-modal>
    </div>
</template>

<script>
import Cookies from 'js-cookie';

const IMAGE_EXTS = ['.jpeg', '.jpg', '.png', '.gif', '.bmp', '.tiff', '.tif', '.webp', '.svg', '.ico', '.raw'];
// Office 格式：无法在线预览，触发下载
const OFFICE_EXTS = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
const PDF_EXTS = ['.pdf'];

function isImageFile(filename) {
    if (!filename) return false;
    const lower = filename.toLowerCase();
    return IMAGE_EXTS.some(ext => lower.endsWith(ext));
}

function isOfficeFile(filename) {
    if (!filename) return false;
    const lower = filename.toLowerCase();
    return OFFICE_EXTS.some(ext => lower.endsWith(ext));
}

function isPdfFile(filename) {
    if (!filename) return false;
    return filename.toLowerCase().endsWith('.pdf');
}

function getBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

// 通用文件图标（SVG data URL），替代空字符串作为非图片文件的 thumbUrl
const FILE_ICON_SVG = 'data:image/svg+xml;utf8,' + encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%2340a9ff">' +
    '<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>' +
    '<polyline points="14 2 14 8 20 8" fill="white"/>' +
    '</svg>'
);

export default {
    name: "Upload",
    props: {
        ID_image: {
            type: Array,
            default: () => []
        },
        count: {
            type: Number,
            default: 1
        },
        isShow: {
            type: Boolean,
            default: false
        },
        imageOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            uploadUrl: process.env.VUE_APP_NEW_URL + '/comapi/UploadApi',
            previewVisible: false,
            previewImage: '',
            hasWarned: false,
        }
    },
    computed: {
        uploadHeaders() {
            const token = Cookies.get('access');
            return token ? { Authorization: 'Bearer ' + token } : {};
        },
        accept() {
            if (this.imageOnly) {
                return '.jpeg,.jpg,.png,.gif,.bmp,.tiff,.tif,.webp,.svg,.ico,.raw';
            }
            return '.jpeg,.jpg,.png,.gif,.bmp,.tiff,.tif,.webp,.svg,.ico,.raw,.mp4,.avi,.mov,.mkv,.wmv,.flv,.webm,.m4v,.3gp,.txt,.doc,.docx,.pdf,.xls,.xlsx,.csv,.zip,.rar,.7z';
        }
    },
    methods: {
        beforeUpload(file, fileList) {
            const currentCount = this.ID_image ? this.ID_image.length : 0;
            const totalCount = currentCount + fileList.length;
            if (totalCount > this.count) {
                if (!this.hasWarned) {
                    this.$message.warning(`最多只能上传${this.count}个文件，当前已有${currentCount}个，您选择了${fileList.length}个，超出限制`);
                    this.hasWarned = true;
                    setTimeout(() => { this.hasWarned = false; }, 2000);
                }
                return false;
            }
            // 非图片文件：设置通用文件图标（不能设置空字符串，会触发 validator 报错）
            if (!isImageFile(file.name)) {
                file.thumbUrl = FILE_ICON_SVG;
            }
            return true;
        },
        async handlePreview(file) {
            const name = file.name || file.url || '';
            const url = file.url
                || (file.response && file.response.data && file.response.data.file_url)
                || '';

            if (!isImageFile(name)) {
                if (!url) return;
                if (isPdfFile(name)) {
                    window.open(url, '_blank');
                } else if (isOfficeFile(name)) {
                    // Office：fetch → blob → a.download 触发下载
                    try {
                        const res = await fetch(url);
                        const blob = await res.blob();
                        const blobUrl = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = blobUrl;
                        a.download = name.split('/').pop() || 'file';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
                    } catch (e) {
                        window.open(url, '_blank');
                    }
                } else {
                    // txt / zip 等：浏览器原生处理
                    window.open(url, '_blank');
                }
                return;
            }
            // 图片：弹窗预览
            if (!file.url && !file.preview) {
                if (file.originFileObj instanceof Blob) {
                    file.preview = await getBase64(file.originFileObj);
                } else if (file.response && file.response.data && file.response.data.file_url) {
                    file.preview = file.response.data.file_url;
                } else {
                    return;
                }
            }
            this.previewImage = file.url || file.preview;
            this.previewVisible = true;
        },
        handleCancel() {
            this.previewVisible = false;
        },
        handleChange({ fileList }) {
            if (fileList.length > this.count) {
                fileList = fileList.slice(0, this.count);
                if (!this.hasWarned) {
                    this.$message.warning(`最多只能上传${this.count}个文件`);
                    this.hasWarned = true;
                    setTimeout(() => { this.hasWarned = false; }, 2000);
                }
            }
            this.$emit('handleChange', fileList);
        }
    }
}
</script>

<style scoped>
.upload-wrapper :deep(.ant-upload-list-picture .ant-upload-list-item) {
    height: auto;
    min-height: 66px;
}
/* 非图片文件的通用图标缩略图大小统一 */
.upload-wrapper :deep(.ant-upload-list-picture .ant-upload-list-item-thumbnail img) {
    object-fit: contain;
    border-radius: 2px;
    background: #f0f7ff;
    padding: 4px;
}
</style>
